/**
 * Product tour controller: auto-plays the "what's new" tour once per account
 * (per TOUR_VERSION, stored server-side via /api/tour) and lets the Support
 * page replay it.
 */
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useAuth } from './AuthContext';
import ProductTour from '../components/tour/ProductTour';
import { TOUR_VERSION } from '../components/tour/tourSteps';

interface ProductTourState {
  startTour: () => void;
}

const ProductTourContext = createContext<ProductTourState>({ startTour: () => undefined });

const apiBase = () => process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE_URL || '';

export const ProductTourProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, token } = useAuth();
  const [open, setOpen] = useState(false);
  const [agentEnabled, setAgentEnabled] = useState(false);

  useEffect(() => {
    if (!token || !user) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    fetch(`${apiBase()}/api/tour`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (cancelled || !data) return;
        setAgentEnabled(!!data.agentEnabled);
        // Auto-play only on desktop-sized screens, for active users, on the main app.
        const desktop = window.matchMedia('(min-width: 768px)').matches;
        const inApp = !window.location.pathname.startsWith('/m') && !window.location.pathname.startsWith('/login');
        if (data.seenVersion !== TOUR_VERSION && desktop && inApp && user.status === 'active') {
          timer = setTimeout(() => !cancelled && setOpen(true), 1500);
        }
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [token, user]);

  const markSeen = useCallback(() => {
    if (!token) return;
    fetch(`${apiBase()}/api/tour`, {
      method: 'PUT',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ version: TOUR_VERSION }),
    }).catch(() => undefined);
  }, [token]);

  const startTour = useCallback(() => setOpen(true), []);
  const close = useCallback(() => {
    setOpen(false);
    markSeen();
  }, [markSeen]);

  return (
    <ProductTourContext.Provider value={{ startTour }}>
      {children}
      {open && <ProductTour agentEnabled={agentEnabled} onClose={close} />}
    </ProductTourContext.Provider>
  );
};

export const useProductTour = () => useContext(ProductTourContext);
